import * as _ from "lodash";
import * as moment from "moment";

interface Select2MatcherParams {
    type: string;
    term: string;
}

interface Select2MatcherData {
    selected: boolean;
    disabled: boolean;
    text: string;
    children: any[];
    element: HTMLElement;
}

var select2Matcher = <any>((params: Select2MatcherParams, data: Select2MatcherData) => {
    var term = params.term;
    var text = data.text;
    // Always return the object if there is nothing to compare
    if ($.trim(term) === "") {
        return data;
    }

    // Check if the text contains the term
    if (text.toLowerCase().indexOf(term.toLocaleLowerCase()) > -1) {
        return data;
    }

    // Do a recursive check for options with children
    if (data.children && data.children.length > 0) {
        // Clone the data object if there are children
        // This is required as we modify the object to remove any non-matches
        var match = $.extend(true, {}, data);

        // Check each child of the option
        for (var c = data.children.length - 1; c >= 0; c--) {
            var child = data.children[c];

            var matches = Utils.Select2.matcher(params, child);

            // If there wasn't a match, remove the object in the array
            if (matches == null) {
                match.children.splice(c, 1);
            }
        }

        // If any children matched, return the new object
        if (match.children.length > 0) {
            return match;
        }

        // If there were no matching children, check just the plain object
        return Utils.Select2.matcher(params, match);
    }

    // If it doesn't contain the term, don't return anything
    return null;
});

export class Utils {
    static _getFormattedValue(value, sensorType, sensorVersion, decimals, conversionFunction) {
        var MAX_NUMBER_DIGITS = 11;
        var ln10 = 2.302585092994046;

        if (_.isNull(value) || _.isUndefined(value) || _.isNaN(value)) return value;

        if (conversionFunction === "on_off") {
            return value === 0 ? "Off" : "On";
        }

        if (sensorType === 18) {
            if (sensorVersion === 16) {
                return value === 0 ? "Off" : "Level " + value;
            } else {
                if (conversionFunction === "cc_water_leak") {
                    return value === 0 ? "OK" : "Leak Detected";
                } else if (conversionFunction === "cc_on_off") {
                    return value === 0 ? "Off" : "On";
                } else if (conversionFunction === "cc_open_closed") {
                    return value === 0 ? "Open" : "Closed";
                } else if (conversionFunction === "cc_overflow") {
                    return value === 0 ? "OK" : "Overflow Detected";
                }
            }
        }

        if (!_.isNull(conversionFunction)) {
            if (conversionFunction.startsWith("_")) {
                var spt = conversionFunction.split("_");
                var nbCF = spt.length;
                for (var i = 0; i < nbCF; i++) {
                    if (value === i) {
                        return spt[i + 1];
                    }
                }
            }
        }

        // Make sure the total number of digits doesn't exceed MAX_NUMBER_DIGITS. Otherwise show it as an exponential
        if (Math.floor(Math.log(value) / ln10) + 1 + decimals > MAX_NUMBER_DIGITS) {
            return value.toExponential(decimals);
        } else {
            return value.toFixed(decimals);
        }
    }

    static validateFormattedValue(value: any): string {
        // Returns the channel value as a string. If the value is not valid, returns "-".
        // Check if the value is a finite number (not NaN, Infinity, or -Infinity)
        if (typeof value === "number" && isFinite(value)) {
            return value.toString(); // Return the string representation of numbers
        }

        // Check if the value is a string and not empty or "null"
        if (typeof value === "string" && value.trim() !== "" && value.trim().toLowerCase() !== "null") {
            return value; // Return the original non-empty string
        }

        // For all other cases (null, undefined, empty string,
        // "null" string, NaN, Infinity, -Infinity), return "-"
        return "-";
    }

    static _getFormatDecimals(realDecimals, value) {
        if (realDecimals === null) {
            if (value > 10e9) {
                return 6;
            } else {
                if (value < 0 && value > -10) {
                    return 3;
                } else if ((value < 10 && value >= 0) || (value > -10 && value < 0)) {
                    return 2;
                } else if ((value < 100 && value > 0) || (value > -100 && value < 0)) {
                    return 1;
                } else {
                    return 0;
                }
            }
        } else {
            return realDecimals;
        }
    }

    static getUserAttributesFromJWT() {
        var lastAuthUser = window.localStorage.getItem(
            `CognitoIdentityServiceProvider.${CONFIG.cognito.userPoolWebClientId}.LastAuthUser`
        );
        if (!lastAuthUser) return null;

        try {
            var userData = JSON.parse(
                window.localStorage.getItem(
                    `CognitoIdentityServiceProvider.${CONFIG.cognito.userPoolWebClientId}.${lastAuthUser}.userData`
                )
            );
        } catch (e) {
            return null;
        }
        if (!userData) return null;

        return _.chain(userData.UserAttributes).keyBy("Name").mapValues("Value").value();
    }

    // From http://stackoverflow.com/questions/8188645/javascript-regex-to-match-a-url-in-a-field-of-text
    static isValidURL(str) {
        var pattern = new RegExp("(http|https)://[\\w-]+(.[\\w-]*)+([\\w.,@?^=%&:/~+#-]*[\\w@?^=%&/~+#-])?", "i");
        return pattern.test(str);
    }

    static isValidEmail(str) {
        var pattern =
            /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
        return pattern.test(str);
    }

    static colors = {
        // red: '#db4c4c',
        // yellow: '#ffc44c',
        // green: 'rgb(99,182,100)',
        // grey: '#60656A',
        aqua: "#A0E7F0",
        maroon: "#7D1610",
        purple: "#6B2A63",
        orange: "#F68D20",
        "light-blue": "#73BCDC",
        "light-red": "#F08A8F",
        red: "#D61F26",
        yellow: "#FFC04F",
        green: "#4CB975",
        blue: "#114257",
        grey: "#60656D",
    };

    static String = {
        capitalize: (str) => {
            return str.charAt(0).toUpperCase() + str.slice(1);
        },
        capitalizeAll: (str) => {
            return str
                .split(" ")
                .map(function (s) {
                    return Utils.String.capitalize(s);
                })
                .join(" ");
        },
        // From: http://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript-jquery
        hash: (str) => {
            var hash = 0;
            if (str.length == 0) return hash;
            for (var i = 0; i < str.length; i++) {
                var char = str.charCodeAt(i);
                hash = (hash << 5) - hash + char;
            }
            return hash;
        },
        // From : https://gist.github.com/mathewbyrne/1280286
        slugify: (text) => {
            return text
                .toString()
                .toLowerCase()
                .replace(/\s+/g, "-") // Replace spaces with -
                .replace(/[^\w\-]+/g, "") // Remove all non-word chars
                .replace(/\-\-+/g, "-") // Replace multiple - with single -
                .replace(/^-+/, "") // Trim - from start of text
                .replace(/-+$/, ""); // Trim - from end of text
        },
    };

    // This is a generic select2 matcher that will match any individual option or the entire optgroup if the term matches
    static Select2 = {
        matcher: select2Matcher,
        defaultOptions: {
            matcher: select2Matcher,
            dropdownAutoWidth: true,
            width: "100%",
        },
    };

    static Time = {
        getChannelLastSeenString: (channelLastTimestamp: number) => {
            var lastSeen = moment.utc(0);
            lastSeen = moment.max(lastSeen, moment.utc(channelLastTimestamp * 1000));
            var timeDiff = moment.utc().valueOf() - lastSeen.valueOf();

            var lastSeenStr;
            if (lastSeen.valueOf() === 0) {
                lastSeenStr = "No Data";
            } else if (timeDiff < 120000) {
                // In case the browser is lagging behind, the server time will be in the future.
                // The time should still be diplayed as minimally 10 seconds in the past, since we round up to
                // the nearest 10 seconds
                lastSeenStr = "Now";
            } else {
                // In case the browser is lagging behind, the server time will be in the future.
                // take the minimum so it will say 'a few seconds ago'
                lastSeenStr = moment(lastSeen).fromNow();
                // Replace "minutes" with "mins" and "minute" with "min" for shorter display
                lastSeenStr = lastSeenStr.replace(/\bminutes\b/g, 'mins').replace(/\bminute\b/g, 'min');
            }

            return lastSeenStr;
        },
        getChannelLastSeenColor: (channelLastTimestamp: number): string => {
            const lastSeen = moment.max(moment.utc(0), moment.utc(channelLastTimestamp * 1000));
            const now = moment.utc();
            const timeDiffMs = now.valueOf() - lastSeen.valueOf();

            // Constants
            const ONE_HOUR_MS = 60 * 60 * 1000;
            const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
            const FIVE_DAYS_MS = 5 * 24 * 60 * 60 * 1000;

            if (lastSeen.valueOf() === 0) {
                return "#000000"; // No Data → Black
            } else if (timeDiffMs < ONE_HOUR_MS) {
                return this.colors.green; // Green until 1 hour
            } else if (timeDiffMs < TWENTY_FOUR_HOURS_MS) {
                return this.colors["light-red"]; // Light red until 24 hours
            } else if (timeDiffMs < FIVE_DAYS_MS) {
                return this.colors.red; // Red from 24 hours to 5 days
            } else {
                return this.colors.maroon; // Maroon for 5+ days
            }
        },
        isChannelLastSeenStale: (channelLastTimestamp: number): boolean => {
            const lastSeen = moment.max(moment.utc(0), moment.utc(channelLastTimestamp * 1000));
            const now = moment.utc();
            const timeDiffMs = now.valueOf() - lastSeen.valueOf();

            const FIVE_DAYS_MS = 5 * 24 * 60 * 60 * 1000;
            return lastSeen.valueOf() !== 0 && timeDiffMs >= FIVE_DAYS_MS;
        },
        getChannelLastSeenOpacity: (channelLastTimestamp: number): number => {
            const lastSeen = moment.max(moment.utc(0), moment.utc(channelLastTimestamp * 1000));
            const now = moment.utc();
            const timeDiffMs = now.valueOf() - lastSeen.valueOf();

            // Constants
            const ONE_DAY_MS = 24 * 60 * 60 * 1000;
            const FIVE_DAYS_MS = 5 * 24 * 60 * 60 * 1000;

            if (lastSeen.valueOf() === 0) {
                return 0.25; // No data →  25% opacity
            } else if (timeDiffMs < ONE_DAY_MS) {
                return 1.0; // Less than 1 day → Full opacity
            } else if (timeDiffMs < FIVE_DAYS_MS) {
                return 0.5; // More than 1 day → 50% opacity
            } else {
                return 0.25; // More than 5 days → 25% opacity
            }
        },
        getIntervalString(
            interval: number,
            offset_seconds: string,
            offset_days: string,
            offset_months: string,
            utcOffset?: string
        ) {
            var hour = offset_seconds ? Math.floor(Number(offset_seconds) / 3600) : 0;
            var minute = offset_seconds ? Math.floor(Number(offset_seconds) % 3600) / 60 : 0;
            var t = moment(hour + ":" + minute, "H:m").format("LT");

            if (utcOffset) {
                t += ` (GMT${utcOffset})`;
            }

            var i = interval ? Number(interval) : "Manual";
            if (i === "Manual") {
                return i;
            } else if (i === 1) {
                return `Daily at ${t}`;
            } else if (i === 2) {
                return `${Utils.Time.numberToDayOfWeek(offset_days)}s at ${t}`;
            } else if (i === 3) {
                return `On the ${Utils.Time.numberToDayOfMonth(offset_days)} day of the month at ${t}`;
            } else {
                return `On ${Utils.Time.numberToMonth(offset_months)} ${Utils.Time.numberToDayOfMonth(
                    offset_days
                )} at ${t}`;
            }
        },
        numberToDayOfWeek(dow: string) {
            switch (dow) {
                case "0":
                    return "Monday";
                case "1":
                    return "Tuesday";
                case "2":
                    return "Wednesday";
                case "3":
                    return "Thursday";
                case "4":
                    return "Friday";
                case "5":
                    return "Saturday";
                case "6":
                    return "Sunday";
            }
        },
        numberToMonth(month: string) {
            switch (month) {
                case "0":
                    return "January";
                case "1":
                    return "February";
                case "2":
                    return "March";
                case "3":
                    return "April";
                case "4":
                    return "May";
                case "5":
                    return "June";
                case "6":
                    return "July";
                case "7":
                    return "August";
                case "8":
                    return "September";
                case "9":
                    return "October";
                case "10":
                    return "November";
                case "11":
                    return "December";
            }
        },
        numberToDayOfMonth(dom: string) {
            if (dom === "last") return "last";

            dom = String(Number(dom) + 1); // 0 indexed
            var l = _.last(dom);
            if (_.includes(["11", "12", "13"], dom)) {
                return `${dom}th`;
            } else if (l === "1") {
                return `${dom}st`;
            } else if (l === "2") {
                return `${dom}nd`;
            } else if (l === "3") {
                return `${dom}rd`;
            } else {
                return `${dom}th`;
            }
        },
    };

    static zeroPad = (num, pad) => {
        var zeros = _.map(_.range(pad), () => "0").join("");
        var str = String(num);
        return zeros.substr(0, pad - str.length) + str;
    };

    /**
     * Dynamically loads a CSS or JS asset into the page.
     *
     * @param url - The URL of the asset to load.
     * @param type - The type of asset ('css' or 'js').
     * @returns A Promise that resolves when the asset is loaded or rejects if an error occurs.
     */
    static loadAsset(url: string, type: "css" | "js"): Promise<void> {
        return new Promise((resolve, reject) => {
            if (type === "css") {
                const link = document.createElement("link");
                link.rel = "stylesheet";
                link.type = "text/css";
                link.href = url;
                link.crossOrigin = "anonymous";

                link.onload = () => resolve();
                link.onerror = (error) => reject(new Error(`Failed to load CSS asset: ${url}`));

                document.head.appendChild(link);
            } else if (type === "js") {
                const script = document.createElement("script");
                script.src = url;
                script.crossOrigin = "anonymous";

                script.onload = () => resolve();
                script.onerror = (error) => reject(new Error(`Failed to load JS asset: ${url}`));

                document.body.appendChild(script);
            } else {
                reject(new Error(`Unsupported asset type: ${type}`));
            }
        });
    }
}

export class Base64 {
    static _keyStr = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=";

    static encode(e) {
        var t = "";
        var n, r, i, s, o, u, a;
        var f = 0;
        e = Base64._utf8_encode(e);
        while (f < e.length) {
            n = e.charCodeAt(f++);
            r = e.charCodeAt(f++);
            i = e.charCodeAt(f++);
            s = n >> 2;
            o = ((n & 3) << 4) | (r >> 4);
            u = ((r & 15) << 2) | (i >> 6);
            a = i & 63;
            if (isNaN(r)) {
                u = a = 64;
            } else if (isNaN(i)) {
                a = 64;
            }
            t =
                t +
                Base64._keyStr.charAt(s) +
                Base64._keyStr.charAt(o) +
                Base64._keyStr.charAt(u) +
                Base64._keyStr.charAt(a);
        }
        return t;
    }

    static decode(e) {
        var t = "";
        var n, r, i;
        var s, o, u, a;
        var f = 0;
        e = e.replace(/[^A-Za-z0-9\+\/\=]/g, "");
        while (f < e.length) {
            s = Base64._keyStr.indexOf(e.charAt(f++));
            o = Base64._keyStr.indexOf(e.charAt(f++));
            u = Base64._keyStr.indexOf(e.charAt(f++));
            a = Base64._keyStr.indexOf(e.charAt(f++));
            n = (s << 2) | (o >> 4);
            r = ((o & 15) << 4) | (u >> 2);
            i = ((u & 3) << 6) | a;
            t = t + String.fromCharCode(n);
            if (u != 64) {
                t = t + String.fromCharCode(r);
            }
            if (a != 64) {
                t = t + String.fromCharCode(i);
            }
        }
        t = Base64._utf8_decode(t);
        return t;
    }

    static _utf8_encode(e) {
        e = e.replace(/\r\n/g, "\n");
        var t = "";
        for (var n = 0; n < e.length; n++) {
            var r = e.charCodeAt(n);
            if (r < 128) {
                t += String.fromCharCode(r);
            } else if (r > 127 && r < 2048) {
                t += String.fromCharCode((r >> 6) | 192);
                t += String.fromCharCode((r & 63) | 128);
            } else {
                t += String.fromCharCode((r >> 12) | 224);
                t += String.fromCharCode(((r >> 6) & 63) | 128);
                t += String.fromCharCode((r & 63) | 128);
            }
        }
        return t;
    }

    static _utf8_decode(e) {
        var t = "";
        var n = 0;
        var r, c1, c2, c3;
        r = c1 = c2 = 0;
        while (n < e.length) {
            r = e.charCodeAt(n);
            if (r < 128) {
                t += String.fromCharCode(r);
                n++;
            } else if (r > 191 && r < 224) {
                c2 = e.charCodeAt(n + 1);
                t += String.fromCharCode(((r & 31) << 6) | (c2 & 63));
                n += 2;
            } else {
                c2 = e.charCodeAt(n + 1);
                c3 = e.charCodeAt(n + 2);
                t += String.fromCharCode(((r & 15) << 12) | ((c2 & 63) << 6) | (c3 & 63));
                n += 3;
            }
        }
        return t;
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/common/utils.ts